import { useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { Role } from '../types';
import { MarkdownContent } from '../components/help/MarkdownContent';
import { PermissionsTable } from '../components/help/PermissionsTable';
import { TableOfContents } from '../components/help/TableOfContents';
import { ROLE_LABELS, ROLE_TAGLINES } from '../lib/permissions';
import { ROLE_CONTENT, CONCEPTS_CONTENT } from '../content/help';

const KNOWN_ROLES: Role[] = [
  'basic-user',
  'reviewer',
  'framework-manager',
  'admin',
];

function resolveRole(raw: string | undefined): Role {
  if (raw && (KNOWN_ROLES as string[]).includes(raw)) {
    return raw as Role;
  }
  if (raw) {
    console.warn(`[help] Unknown role "${raw}", falling back to basic-user.`);
  }
  return 'basic-user';
}

export function HelpPage() {
  const { user } = useAuth();
  const role = resolveRole(user?.role);
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[240px_minmax(0,1fr)] gap-8">
      <aside>
        <TableOfContents containerRef={contentRef} contentKey={role} />
      </aside>
      <div ref={contentRef}>
        <header className="mb-8">
          <p className="text-xs uppercase tracking-wide text-wolf-400">
            Your role: {ROLE_LABELS[role]}
          </p>
          <h1 className="text-3xl font-bold text-wolf-100 mt-1">
            {ROLE_LABELS[role]} guide
          </h1>
          <p className="text-wolf-300 mt-2">{ROLE_TAGLINES[role]}</p>
        </header>

        <section aria-labelledby="permissions-heading" className="mb-10">
          <h2
            id="permissions-heading"
            className="text-xl font-semibold text-wolf-100 mb-3"
          >
            Permissions at a glance
          </h2>
          <PermissionsTable activeRole={role} />
        </section>

        <MarkdownContent source={ROLE_CONTENT[role]} />

        <hr className="my-10 border-wolf-700" />

        <MarkdownContent
          source={CONCEPTS_CONTENT.replace(
            /\{\{MCP_URL\}\}/g,
            `${window.location.origin}/mcp`,
          )}
        />
      </div>
    </div>
  );
}
